/**
 * Shared event type for UI components
 * This type is used across all events UI components for consistency
 */
export type EventUI = {
  id: string;
  slug: string;
  title: string;
  excerpt?: string;
  startDateTime: string;
  endDateTime?: string;
  location?: string;
  coverImage?: { src: string; alt?: string };
  type?: string;
  typeLabel?: string;
  weeztixShopUrl?: string;
  featured?: boolean;
  speaker?: string;
  youtubeUrl?: string;
};

export type ViewMode = "timeline" | "list";

export type QuickFilter = "all" | "week" | "month";

/**
 * Event type options from Storyblok with Dutch labels
 */
export const EVENT_TYPE_LABELS: Record<string, string> = {
  service: "Dienst",
  event: "Event",
  youth: "Jongeren",
  teens: "Tieners",
  disciples: "Discipelen",
  prayer: "Gebed",
  community: "Community",
  teaching: "Onderwijs",
};

/**
 * Get display label for event type
 */
export function getEventTypeLabel(type: string | undefined): string {
  if (!type) return "";
  return EVENT_TYPE_LABELS[type] || type;
}

/**
 * Format date for display in nl-NL locale
 */
export function formatEventDate(dateString: string) {
  const date = new Date(dateString);
  return {
    day: date.getDate(),
    dayName: date.toLocaleDateString("nl-NL", { weekday: "short" }),
    dayNameLong: date.toLocaleDateString("nl-NL", { weekday: "long" }),
    month: date.toLocaleDateString("nl-NL", { month: "short" }).toUpperCase(),
    monthLong: date.toLocaleDateString("nl-NL", { month: "long" }),
    year: date.getFullYear(),
    time: date.toLocaleTimeString("nl-NL", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    monthKey: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
    iso: date.toISOString(),
  };
}

/**
 * Group events by month
 */
export function groupEventsByMonth(events: EventUI[]) {
  const groups: Record<string, { label: string; events: EventUI[] }> = {};

  for (const event of events) {
    const { monthKey, monthLong, year } = formatEventDate(event.startDateTime);
    if (!groups[monthKey]) {
      groups[monthKey] = {
        label: `${monthLong} ${year}`,
        events: [],
      };
    }
    groups[monthKey].events.push(event);
  }

  return Object.entries(groups)
    .toSorted(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({ monthKey: key, ...value }));
}

/**
 * Get available months from events
 */
export function getAvailableMonths(events: EventUI[]) {
  const months = new Map<string, { key: string; label: string }>();

  for (const event of events) {
    const { monthKey, month, year } = formatEventDate(event.startDateTime);
    if (!months.has(monthKey)) {
      months.set(monthKey, { key: monthKey, label: `${month} ${year}` });
    }
  }

  return [...months.values()].toSorted((a, b) => a.key.localeCompare(b.key));
}
